/*
 * <splide-component>: wraps a .splide element and configures Splide (https://splidejs.com, MIT)
 * from data-* attributes, matching the reference theme's attribute API:
 *   data-type, data-type-mobile, data-autoplay, data-autoplay-speed (s), data-drag, data-focus,
 *   data-arrows, data-arrows-color, data-pagination, data-dots-color, data-omit-end,
 *   data-slides-desktop / data-slides-mobile, data-per-move-desktop / -mobile,
 *   data-gap-desktop / -mobile (px), data-side-padding-desktop / -mobile (px),
 *   data-padding-calc-desktop / -mobile, data-destroy-desktop, data-destroy-mobile,
 *   data-auto-width, data-pause-videos.
 * Requires assets/splide.min.js to be loaded first.
 */
class SplideComponent extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('.splide');
    if (!this.root || typeof Splide === 'undefined') return;
    this.mount();
    this.onSectionLoad = (event) => {
      if (event.target && event.target.contains(this)) this.remount();
    };
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    this.splide?.destroy(true);
    this.splide = null;
  }

  bool(name, fallback) {
    const value = this.dataset[name];
    if (value === undefined) return fallback;
    return value === 'true';
  }

  num(name, fallback) {
    const value = parseFloat(this.dataset[name]);
    return Number.isNaN(value) ? fallback : value;
  }

  padding(side) {
    const value = this.num(`sidePadding${side}`, 0);
    if (!value) return 0;
    if (this.bool(`paddingCalc${side}`, false)) return { left: 0, right: value };
    return { left: value, right: value };
  }

  buildOptions() {
    const arrowsColor = this.dataset.arrowsColor ? ` color-${this.dataset.arrowsColor}` : '';
    const dotsColor = this.dataset.dotsColor ? ` color-${this.dataset.dotsColor} dots-custom-color` : '';

    const desktop = {
      type: this.dataset.type || 'slide',
      perPage: this.num('slidesDesktop', 3),
      perMove: this.num('perMoveDesktop', 1),
      gap: `${this.num('gapDesktop', 30)}px`,
      padding: this.padding('Desktop'),
      destroy: this.bool('destroyDesktop', false),
    };

    const mobile = {
      type: this.dataset.typeMobile || this.dataset.type || 'slide',
      perPage: this.num('slidesMobile', 1),
      perMove: this.num('perMoveMobile', 1),
      gap: `${this.num('gapMobile', 15)}px`,
      padding: this.padding('Mobile'),
      destroy: this.bool('destroyMobile', false),
    };

    return {
      ...desktop,
      autoplay: this.bool('autoplay', false),
      interval: this.num('autoplaySpeed', 5) * 1000,
      pauseOnHover: true,
      drag: this.dataset.drag === 'free' ? 'free' : this.bool('drag', true),
      focus: this.dataset.focus === 'center' ? 'center' : this.num('focus', 0),
      trimSpace: this.bool('trimSpace', true),
      omitEnd: this.bool('omitEnd', true),
      arrows: this.bool('arrows', true),
      pagination: this.bool('pagination', true),
      autoWidth: this.bool('autoWidth', false),
      rewind: this.bool('rewind', false),
      speed: 600,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      mediaQuery: 'max',
      breakpoints: { 749: mobile },
      classes: {
        arrows: 'splide__arrows',
        arrow: `splide__arrow${arrowsColor}`,
        prev: 'splide__arrow--prev',
        next: 'splide__arrow--next',
        pagination: 'splide__pagination',
        page: `splide__pagination__page${dotsColor}`,
      },
    };
  }

  mount() {
    const options = this.buildOptions();
    this.splide = new Splide(this.root, options);

    this.splide.on('mounted move', () => this.markActive());
    this.splide.on('moved', () => {
      if (this.bool('pauseVideos', false) && window.pauseAllMedia) window.pauseAllMedia();
    });
    this.splide.on('destroy', () => {
      this.root.classList.remove('is-overflow');
      this.querySelectorAll('.splide__slide').forEach((slide) => slide.classList.remove('is-instant-active'));
    });

    this.splide.mount();
    this.markActive();
  }

  remount() {
    this.splide?.destroy(true);
    this.root = this.querySelector('.splide');
    if (this.root) this.mount();
  }

  markActive() {
    if (!this.splide) return;
    const slides = this.root.querySelectorAll('.splide__slide');
    slides.forEach((slide) => slide.classList.remove('is-instant-active'));
    const active = slides[this.splide.index];
    if (!active) return;
    active.classList.add('is-instant-active');
    const height = active.querySelector('.splide__slide__container')?.offsetHeight || active.offsetHeight;
    if (height) this.style.setProperty('--active-slide-height', `${height}px`);
  }
}

if (!customElements.get('splide-component')) customElements.define('splide-component', SplideComponent);
